/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useHistory } from "react-router-dom";
import { Row as RowType } from "react-table";
import { Box, Heading, Loader } from "@nordcloud/gnui";
import { QueryPagination, Table } from "~/components";
import { ProductAdd } from "~/components/Product/ProductAdd";
import { FilteredSearch } from "~/components/Search/FilteredSearch";
import { Product } from "~/models/product";
import { ACTIONS, Can, ROUTES } from "~/routing";
import useSortParams from "~/tools/hooks/useSortParams";
import { useProductCatalog } from "./hooks/useProductCatalog";
import { ProductCatalogListColumns } from "./ProductCatalogList";

function ProductCatalogPage() {
  const [sortParams, setSorting] = useSortParams("name");
  const { productCatalog, isLoading } = useProductCatalog(
    sortParams.sortBy,
    sortParams.order
  );

  const columns = React.useMemo(() => ProductCatalogListColumns, []);

  const history = useHistory();

  const handleRowKeyPress = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    selected: Product
  ) => {
    if (e.key === "Enter") {
      onRedirectHandler(selected);
    }
  };

  const onRedirectHandler = (row: Product) => {
    history.push(`${ROUTES.productCatalog.index}/${row.id}`);
  };

  return (
    <Container>
      <Row>
        <Col>
          <Box
            css={{ display: "flex", alignItems: "center" }}
            spacing="spacing04"
          >
            <Heading level={2}>Product Catalog</Heading>
            <Can action={ACTIONS.ADD_PRODUCT}>
              <ProductAdd />
            </Can>
          </Box>
          <Box spacing="spacing04">
            <Box boxStyle="lightGrey" spacing="spacing04">
              <FilteredSearch
                mode="products"
                endpoint="products"
                redirectLink={ROUTES.productCatalog.index}
                options={{ keys: ["id", "name"] }}
              />
            </Box>
            {isLoading ? (
              <Loader inContent />
            ) : (
              <>
                <Table
                  getRowProps={(row: RowType) => ({
                    onClick: () => onRedirectHandler(row.original as Product),
                    onKeyPress: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                      handleRowKeyPress(e, row.original as Product),
                    className: "interactive",
                    tabIndex: 0,
                    role: "button",
                  })}
                  columns={columns}
                  data={productCatalog.list || []}
                  defaultSort={[{ id: "name", desc: false }]}
                  serverSideSort={setSorting}
                />
                <QueryPagination count={productCatalog.totalCount} />
              </>
            )}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export { ProductCatalogPage };
